// Pure state for one Kick channel's chat feed, driven by kick-chat-service SSE events.
//
// Event contract (everything kick-chat-service sends on the default SSE channel):
//   { type: 'message', message: { id, type, content, sender: { id: <number>, username, identity }, metadata }, timestamp }
//       message.type: message | reply | subscription | gifted_subs | gifted_subs_lucky | celebration
//   { type: 'delete', deleted_msg_id }
//   { type: 'user.banned' | 'user.timedout' | 'user.unbanned', user: { id: <number>, username },
//     duration?, expires_at?, banned_by?, unbanned_by? }
// plus the named SSE events `connected` and `heartbeat`, which the stream hook handles.
//
// User IDs arrive as numbers and are normalised to strings everywhere in this state.

export const SEPARATOR_ID = '__separator__';
const MAX_RECENT_CHATTERS = 50;
const CHAT_MESSAGE_TYPES = new Set(['message', 'reply']);

export const initialKickChatState = {
  messages: [], // render order, including moderation notices and the "New Messages" separator
  seenIds: new Set(), // ids currently in `messages`; replayed history after a reconnect is deduped against this
  bannedUserIds: new Set(),
  timedOutUntil: {}, // userId -> expiry (ms since epoch)
  recentChatters: [], // usernames for @mention autocomplete, most recent last
  replaying: true, // between (re)connecting and the first heartbeat, the server is replaying its history buffer
  historyLoaded: false, // first heartbeat seen at least once
  latestTimestamp: 0, // newest message timestamp we have, used to skip stale replays after a reconnect
  viewerRestriction: null, // { type: 'banned' } | { type: 'timeout', expiresAt } for the logged-in viewer here
};

const toId = (id) => (id === undefined || id === null ? null : String(id));
const toTime = (value) => {
  const t = typeof value === 'number' ? value : Date.parse(value);
  return Number.isFinite(t) ? t : null;
};

export const isUserBanned = (state, userId) => state.bannedUserIds.has(toId(userId));
export const isUserTimedOut = (state, userId, now = Date.now()) => (state.timedOutUntil[toId(userId)] || 0) > now;

// The user's most recent chat message that hasn't been deleted (for "Delete Last Message"), or null
export function lastDeletableMessageId(state, userId) {
  const id = toId(userId);
  for (let i = state.messages.length - 1; i >= 0; i -= 1) {
    const m = state.messages[i];
    if (m.id && !m.deleted && CHAT_MESSAGE_TYPES.has(m.type) && toId(m.sender?.id) === id) return m.id;
  }
  return null;
}

function appendMessages(state, newMessages, maxMessages) {
  let messages = [...state.messages, ...newMessages];
  const seenIds = new Set(state.seenIds);
  newMessages.forEach((m) => seenIds.add(m.id));
  if (messages.length > maxMessages) {
    const removed = messages.slice(0, messages.length - maxMessages);
    removed.forEach((m) => seenIds.delete(m.id));
    messages = messages.slice(-maxMessages);
  }
  return { ...state, messages, seenIds };
}

// Hide a user's messages: removed for regular viewers, shown struck-through to moderators
function hideMessagesFrom(messages, userId, canModerate) {
  // Messages already deleted individually aren't tagged, so an unban won't bring them back
  return canModerate
    ? messages.map((m) => (toId(m.sender?.id) === userId && !m.deleted ? { ...m, deleted: true, deletedByModeration: true } : m))
    : messages.filter((m) => toId(m.sender?.id) !== userId);
}

function handleModerationEvent(state, data, { now, viewerId, canModerate, maxMessages }) {
  const userId = toId(data.user?.id);
  if (!userId) return state;
  const username = data.user?.username || 'Someone';

  const bannedUserIds = new Set(state.bannedUserIds);
  const timedOutUntil = { ...state.timedOutUntil };
  let { messages, viewerRestriction } = state;
  let notice;

  if (data.type === 'user.banned') {
    bannedUserIds.add(userId);
    delete timedOutUntil[userId];
    messages = hideMessagesFrom(messages, userId, canModerate);
    if (userId === viewerId) viewerRestriction = { type: 'banned' };
    notice = {
      moderationType: 'ban',
      content: `${username} was banned${data.banned_by?.username ? ` by ${data.banned_by.username}` : ''}`,
    };
  } else if (data.type === 'user.timedout') {
    // expires_at is authoritative; `duration` (the fallback) is in minutes, as Kick sends it
    const expiresAt = toTime(data.expires_at) ?? now + (Number(data.duration) || 0) * 60000;
    timedOutUntil[userId] = expiresAt;
    messages = hideMessagesFrom(messages, userId, canModerate);
    if (userId === viewerId) viewerRestriction = { type: 'timeout', expiresAt: new Date(expiresAt).toISOString() };
    const minutes = Math.max(1, Math.round((expiresAt - now) / 60000));
    notice = {
      moderationType: 'timeout',
      content: `${username} was timed out for ${minutes} minute${minutes !== 1 ? 's' : ''}${data.banned_by?.username ? ` by ${data.banned_by.username}` : ''}`,
      expiresAt: new Date(expiresAt).toISOString(),
    };
  } else {
    bannedUserIds.delete(userId);
    delete timedOutUntil[userId];
    // Bring back only what moderation hid, not messages a moderator deleted individually
    if (canModerate) {
      messages = messages.map((m) =>
        toId(m.sender?.id) === userId && m.deletedByModeration ? { ...m, deleted: false, deletedByModeration: false } : m
      );
    }
    if (userId === viewerId) viewerRestriction = null;
    notice = {
      moderationType: 'unban',
      content: `${username} was unbanned${data.unbanned_by?.username ? ` by ${data.unbanned_by.username}` : ''}`,
    };
  }

  // Kept for everyone; the component only renders moderation notices for moderators
  const systemMessage = {
    id: `${notice.moderationType}-${userId}-${now}`,
    type: 'moderation',
    userId,
    username,
    timestamp: new Date(now).toISOString(),
    ...notice,
  };
  return appendMessages({ ...state, messages, bannedUserIds, timedOutUntil, viewerRestriction }, [systemMessage], maxMessages);
}

function handleChatMessage(state, data, { now, maxAgeMs, maxMessages }) {
  const msg = data.message;
  if (!msg?.id || !msg.sender?.username || state.seenIds.has(msg.id)) return state;

  const receivedAt = toTime(data.timestamp) ?? toTime(msg.created_at) ?? now;
  if (state.replaying) {
    // Initial history: skip anything older than the configured window
    if (!state.historyLoaded && now - receivedAt > maxAgeMs) return state;
    // Replay after a reconnect: anything not newer than what we already have is old history we trimmed
    if (state.historyLoaded && receivedAt <= state.latestTimestamp) return state;
  }

  const senderId = toId(msg.sender.id);
  if (state.bannedUserIds.has(senderId) || (state.timedOutUntil[senderId] || 0) > now) return state;

  let { recentChatters } = state;
  if (CHAT_MESSAGE_TYPES.has(msg.type)) {
    recentChatters = [...recentChatters.filter((name) => name !== msg.sender.username), msg.sender.username].slice(-MAX_RECENT_CHATTERS);
  }

  const next = appendMessages({ ...state, recentChatters }, [msg], maxMessages);
  return { ...next, latestTimestamp: Math.max(state.latestTimestamp, receivedAt) };
}

/**
 * Actions:
 *  { type: 'connecting' }        a (re)connect started; the server will replay its history buffer
 *  { type: 'heartbeat' }         first heartbeat after connecting ends the replay
 *  { type: 'event', data, now, viewerId, canModerate, maxAgeMs, maxMessages }   one parsed SSE event
 *  { type: 'moderated', action, userId, messageId, durationMinutes, now, canModerate }   optimistic local update
 *                                after this viewer's own moderation request succeeds
 */
export function kickChatReducer(state, action) {
  switch (action.type) {
    case 'connecting':
      return state.replaying ? state : { ...state, replaying: true };

    case 'heartbeat': {
      if (!state.replaying) return state;
      if (state.historyLoaded) return { ...state, replaying: false };
      const separator = { id: SEPARATOR_ID, type: 'separator' };
      return { ...state, replaying: false, historyLoaded: true, messages: [...state.messages, separator] };
    }

    case 'event': {
      const { data } = action;
      const context = { ...action, viewerId: toId(action.viewerId) };
      if (data?.type === 'message') return handleChatMessage(state, data, context);
      if (data?.type === 'delete' && data.deleted_msg_id) {
        return {
          ...state,
          messages: state.messages.map((m) => (m.id === data.deleted_msg_id ? { ...m, deleted: true } : m)),
        };
      }
      if (data?.type === 'user.banned' || data?.type === 'user.timedout' || data?.type === 'user.unbanned') {
        return handleModerationEvent(state, data, context);
      }
      return state;
    }

    case 'moderated': {
      const userId = toId(action.userId);
      const bannedUserIds = new Set(state.bannedUserIds);
      const timedOutUntil = { ...state.timedOutUntil };
      switch (action.action) {
        case 'ban':
          bannedUserIds.add(userId);
          delete timedOutUntil[userId];
          return { ...state, bannedUserIds, timedOutUntil };
        case 'timeout':
          timedOutUntil[userId] = action.now + (action.durationMinutes || 10) * 60000;
          return { ...state, timedOutUntil };
        case 'unban':
        case 'untimeout':
          bannedUserIds.delete(userId);
          delete timedOutUntil[userId];
          return { ...state, bannedUserIds, timedOutUntil };
        case 'delete':
          return { ...state, messages: state.messages.map((m) => (m.id === action.messageId ? { ...m, deleted: true } : m)) };
        default:
          return state;
      }
    }

    default:
      return state;
  }
}
